/**
 * Phase 11c — Runtime theme fetch + CSS var injection.
 *
 * On boot (and on tenant switch), we fetch `GET /api/tenant/theme` which
 * resolves via Host header (custom domain) or `?tenant_slug=` fallback,
 * or defaults to Quanta. The returned `resolved` doc has every field
 * populated — we blindly apply it.
 *
 * We DO NOT modify `--quanta-*` variables directly on the raw CSS file;
 * we set them on `document.documentElement` so specificity wins without
 * a stylesheet rewrite.
 */
const DEFAULT_BASE = (
  typeof window !== "undefined"
    ? (process.env.REACT_APP_BACKEND_URL || "")
    : ""
);

const COLOR_KEYS = ["navy", "blue", "cyan", "muted", "canvas", "border"];

export const QUANTA_DEFAULTS = {
  product_display_name: "Quanta",
  tagline: "Your day, understood.",
  logo_primary_url: null,
  logo_reversed_url: null,
  logo_symbol_url: null,
  favicon_url: null,
  email_header_banner_url: null,
  email_from_name: null,
  font_family_body:
    "InterVariable, Inter, ui-sans-serif, system-ui, sans-serif",
  font_family_display:
    "InterVariable, Inter, ui-sans-serif, sans-serif",
  webfont_urls: [],
  colors: {
    navy: "#0B2545",
    blue: "#2563EB",
    cyan: "#06B6D4",
    muted: "#52647A",
    canvas: "#F7F9FC",
    border: "#CBD5E1",
  },
};

/**
 * Fetch the resolved theme for the current tenant.
 * @param {string} [tenantSlug]  Optional query fallback (previews).
 */
export async function fetchTheme(tenantSlug) {
  const base = DEFAULT_BASE.replace(/\/$/, "");
  const url = new URL(`${base}/api/tenant/theme`, window.location.origin);
  if (tenantSlug) url.searchParams.set("tenant_slug", tenantSlug);
  try {
    const r = await fetch(url.toString(), { credentials: "include" });
    if (!r.ok) return { resolved: QUANTA_DEFAULTS };
    return await r.json();
  } catch (_err) {
    return { resolved: QUANTA_DEFAULTS };
  }
}

/** Convert a `/api/brand/...` relative URL to an absolute preview URL. */
function absolutize(url) {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  const base = DEFAULT_BASE.replace(/\/$/, "");
  if (url.startsWith("/")) return `${base}${url}`;
  return url;
}

/**
 * Apply the theme to the DOM: CSS vars, <title>, favicon, webfonts.
 * Idempotent — safe to call multiple times.
 */
export function applyTheme(resolved) {
  if (!resolved || typeof document === "undefined") return;
  const root = document.documentElement;
  const colors = resolved.colors || QUANTA_DEFAULTS.colors;
  for (const k of COLOR_KEYS) {
    const v = colors[k];
    if (typeof v === "string" && /^#[0-9a-fA-F]{6}$/.test(v)) {
      root.style.setProperty(`--quanta-${k}`, v);
    }
  }
  // Document title.
  if (resolved.product_display_name) {
    document.title = resolved.product_display_name;
  }
  // Favicon.
  if (resolved.favicon_url) {
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = absolutize(resolved.favicon_url);
  }
  // Font families — assign to the root so cascading picks them up.
  if (resolved.font_family_body) {
    root.style.setProperty("--quanta-font-body", resolved.font_family_body);
  }
  if (resolved.font_family_display) {
    root.style.setProperty("--quanta-font-display", resolved.font_family_display);
  }
  // Webfont links — add each URL as a <link> once. Track injected urls to
  // avoid duplicates on re-apply.
  const seen = new Set(
    Array.from(document.querySelectorAll('link[data-quanta-webfont="1"]')).map(
      (n) => n.getAttribute("href"),
    ),
  );
  for (const url of resolved.webfont_urls || []) {
    if (!/^https:\/\//.test(url)) continue;
    if (seen.has(url)) continue;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = url;
    link.crossOrigin = "anonymous";
    link.setAttribute("data-quanta-webfont", "1");
    document.head.appendChild(link);
  }
}

export function themedLogoUrl(theme, variant = "primary") {
  const key =
    variant === "reversed"
      ? "logo_reversed_url"
      : variant === "symbol"
        ? "logo_symbol_url"
        : "logo_primary_url";
  const v = theme?.[key];
  return v ? absolutize(v) : null;
}
